import { useEffect, useState } from 'react';
import { Package, Truck, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { getOrders, updateOrderStatus } from '@/lib/api';
import { formatPrice, formatDate } from '@/lib/utils';
import { toast } from 'sonner';

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const res = await getOrders();
      setOrders(res.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenStatusDialog = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setTrackingNumber(order.tracking_number || '');
    setStatusDialogOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder) return;

    try {
      await updateOrderStatus(selectedOrder.order_id, newStatus, trackingNumber || null);
      toast.success('Order status updated');
      setStatusDialogOpen(false);
      loadOrders();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-stone-100 text-stone-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Package,
      processing: Package,
      shipped: Truck,
      delivered: CheckCircle,
      cancelled: XCircle
    };
    const Icon = icons[status] || Package;
    return <Icon className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="h-8 skeleton rounded w-64 mb-8" />
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-32 skeleton rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" data-testid="admin-orders">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="mb-8">
          <h1 className="text-4xl font-medium mb-2">Order Management</h1>
          <p className="text-stone-600">{orders.length} total orders</p>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-16 bg-white border border-stone-200 rounded-lg">
            <Package className="h-16 w-16 mx-auto mb-4 text-stone-300" />
            <p className="text-lg text-stone-600">No orders yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.order_id}
                className="bg-white border border-stone-200 rounded-lg p-6 hover:border-stone-300 transition-colors"
                data-testid={`order-${order.order_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-light">Order #{order.order_id}</h3>
                      <Badge className={getStatusColor(order.status)}>
                        {getStatusIcon(order.status)}
                        <span className="ml-1">{order.status}</span>
                      </Badge>
                      {order.payment_status === 'paid' && (
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          Paid
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-stone-600">
                      {formatDate(order.created_at)} • {order.customer_email}
                    </p>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-xl font-light mb-2">{formatPrice(order.total || 0)}</p>
                    <Dialog open={statusDialogOpen && selectedOrder?.order_id === order.order_id} onOpenChange={setStatusDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenStatusDialog(order)}
                          data-testid={`update-status-${order.order_id}`}
                        >
                          Update Status
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Update Order Status</DialogTitle>
                          <DialogDescription>
                            Change the status of order #{order.order_id}
                          </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4">
                          <div>
                            <Label>Order Status</Label>
                            <Select value={newStatus} onValueChange={setNewStatus}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="processing">Processing</SelectItem>
                                <SelectItem value="shipped">Shipped</SelectItem>
                                <SelectItem value="delivered">Delivered</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {(newStatus === 'shipped' || newStatus === 'delivered') && (
                            <div>
                              <Label>Tracking Number (Optional)</Label>
                              <Input
                                value={trackingNumber}
                                onChange={(e) => setTrackingNumber(e.target.value)}
                                placeholder="Enter tracking number"
                              />
                            </div>
                          )}
                        </div>
                        
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleUpdateStatus} className="btn-primary">
                            Update Status
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Order Items */}
                <div className="border-t border-stone-200 pt-4 mt-4">
                  <p className="text-sm font-medium text-stone-600 mb-3">
                    {order.items?.length || 0} {(order.items?.length || 0) === 1 ? 'item' : 'items'}
                  </p>
                  <div className="space-y-3">
                    {order.items?.map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-light">{item.name || item.product_name || 'Unknown Item'}</p>
                          <p className="text-xs text-stone-500">
                            Qty: {item.quantity} × {formatPrice(item.price)}
                          </p>
                        </div>
                        <p className="text-sm font-light">
                          {formatPrice((item.price || 0) * (item.quantity || 1))}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Shipping Info */}
                <div className="border-t border-stone-200 pt-4 mt-4 grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">
                      Shipping Address
                    </p>
                    <p className="text-sm text-stone-700">{order.customer_name}</p>
                    <p className="text-sm text-stone-600">{order.shipping_address.address}</p>
                    <p className="text-sm text-stone-600">
                      {order.shipping_address.city} {order.shipping_address.postal_code}
                    </p>
                    <p className="text-sm text-stone-600">{order.shipping_address.country}</p>
                  </div>
                  
                  {order.tracking_number && (
                    <div>
                      <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">
                        Tracking Number
                      </p>
                      <p className="text-sm font-medium text-stone-900">{order.tracking_number}</p>
                    </div>
                  )}
                </div>

                {/* Order Summary */}
                <div className="border-t border-stone-200 pt-4 mt-4">
                  <div className="flex justify-end">
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between gap-8">
                        <dt className="text-stone-600">Subtotal</dt>
                        <dd className="font-light">{formatPrice(order.subtotal || 0)}</dd>
                      </div>
                      <div className="flex justify-between gap-8">
                        <dt className="text-stone-600">GST (8%)</dt>
                        <dd className="font-light">{formatPrice(order.tax || 0)}</dd>
                      </div>
                      <div className="flex justify-between gap-8">
                        <dt className="text-stone-600">Shipping</dt>
                        <dd className="font-light">{formatPrice(order.shipping || 0)}</dd>
                      </div>
                      <div className="flex justify-between gap-8 pt-2 border-t border-stone-200">
                        <dt className="font-medium">Total</dt>
                        <dd className="font-medium">{formatPrice(order.total || 0)}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
